import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Heart, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

const GIVEBUTTER_URL = "https://givebutter.com/XT2qPn";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isCompaniesOpen, setIsCompaniesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainNavLinks = [
    { href: '#impact', label: 'Impact', isScroll: true },
    { href: '#partners', label: 'Partners', isScroll: true },
    { href: '/upcoming-initiatives', label: 'Upcoming Initiatives', isScroll: false },
    { href: '#mission', label: 'Our Mission', isScroll: true },
    { href: '#about', label: 'About', isScroll: true },
    { href: '#contact', label: 'Contact', isScroll: true },
  ];

  const projectLinks = [
    { href: '/mentoring', label: 'Mentoring Program' },
    { href: '/financial-education', label: 'Financial Education' },
    { href: '/housing', label: 'Housing Project' },
  ];

  const companyLinks = [
    { href: 'https://prestigiouscompanies.org', label: 'Prestigious Companies', external: true },
    { href: '/travel-club', label: 'Prestigious Travel Club Inc', external: false },
    { href: 'https://prestigious-marketing.com/', label: 'Prestigious Marketing', external: true },
    { href: 'https://easyquickfund.com', label: 'Easy Quick Fund', external: true },
    { href: 'https://prestigious-tv.com', label: 'Prestigious TV', external: true },
    { href: 'https://hub.prestigiouscompanies.org', label: 'PTC Client Hub', external: true },
  ];

  const handleNavClick = (link) => {
    if (link.external) {
      window.open(link.href, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      return;
    }
    if (link.isScroll) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(link.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(link.href);
    }
    setIsMobileMenuOpen(false);
    setIsProjectsOpen(false);
    setIsCompaniesOpen(false);
  };

  const handleDonate = () => {
    window.open(GIVEBUTTER_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <motion.nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled ? 'glass-header py-3' : 'bg-white/95 backdrop-blur-sm py-4'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="/" 
              onClick={(e) => { e.preventDefault(); navigate('/'); }}
              className="flex items-center gap-3"
              data-testid="logo-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_6b3489be-920b-4f06-be49-83f9903cfac6/artifacts/sacmjjq6_image.png"
                alt="The Prestigious Travel Club Foundation"
                className="h-12 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-5">
              {/* Impact */}
              <button
                onClick={() => handleNavClick(mainNavLinks[0])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                Impact
              </button>

              {/* Projects Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsProjectsOpen(!isProjectsOpen)}
                  onMouseEnter={() => setIsProjectsOpen(true)}
                  className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors flex items-center gap-1"
                  data-testid="nav-projects"
                >
                  Projects
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProjectsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isProjectsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    onMouseLeave={() => setIsProjectsOpen(false)}
                  >
                    {projectLinks.map((link) => (
                      <button
                        key={link.href}
                        onClick={() => handleNavClick(link)}
                        className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#1E3A8A] transition-colors"
                      >
                        {link.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Partners */}
              <button
                onClick={() => handleNavClick(mainNavLinks[1])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                Partners
              </button>

              {/* Upcoming Initiatives */}
              <button
                onClick={() => handleNavClick(mainNavLinks[2])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                Upcoming Initiatives
              </button>

              {/* Our Mission */}
              <button
                onClick={() => handleNavClick(mainNavLinks[3])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                Our Mission
              </button>

              {/* Prestigious Companies Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsCompaniesOpen(!isCompaniesOpen)}
                  onMouseEnter={() => setIsCompaniesOpen(true)}
                  className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors flex items-center gap-1"
                  data-testid="nav-companies"
                >
                  Prestigious Companies
                  <ChevronDown className={`w-4 h-4 transition-transform ${isCompaniesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isCompaniesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    onMouseLeave={() => setIsCompaniesOpen(false)}
                  >
                    {companyLinks.map((link, idx) => (
                      <button
                        key={link.href}
                        onClick={() => handleNavClick(link)}
                        className={`w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#1E3A8A] transition-colors flex items-center justify-between ${
                          idx === 0 ? 'border-b border-slate-100 font-semibold' : ''
                        }`}
                      >
                        {link.label}
                        {link.external && (
                          <span className="text-xs text-slate-400">↗</span>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>

              {/* About */}
              <button
                onClick={() => handleNavClick(mainNavLinks[4])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                About
              </button>

              {/* Contact */}
              <button
                onClick={() => handleNavClick(mainNavLinks[5])}
                className="font-medium text-slate-700 hover:text-[#1E3A8A] transition-colors"
              >
                Contact
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-800" />
              ) : (
                <Menu className="w-6 h-6 text-slate-800" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed inset-x-0 top-20 z-30 bg-white border-b border-slate-200 shadow-lg lg:hidden max-h-[80vh] overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-4 space-y-2">
            {mainNavLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left py-3 text-slate-700 hover:text-[#1E3A8A] font-medium border-b border-slate-100"
              >
                {link.label}
              </button>
            ))}
            
            {/* Projects Section */}
            <div className="py-2">
              <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Projects</p>
              {projectLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="block w-full text-left py-3 pl-4 text-slate-700 hover:text-[#1E3A8A] font-medium border-b border-slate-100"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Companies Section */}
            <div className="py-2">
              <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Prestigious Companies</p>
              {companyLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link)}
                  className="block w-full text-left py-3 pl-4 text-slate-700 hover:text-[#1E3A8A] font-medium border-b border-slate-100 flex items-center justify-between"
                >
                  {link.label}
                  {link.external && <span className="text-xs text-slate-400">↗</span>}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default Navigation;
